<template>
  <Admin title="Delete Purchase Invoice Documents">
    <v-main class="grey lighten-5">
      <v-container fluid class="pa-4">
        <v-row>
          <v-col cols="12">
            <v-card class="white">
              <v-card-title class="pa-3 error white--text">
                <v-icon dark small class="mr-2">mdi-delete</v-icon>
                Delete Purchase Invoice Documents for Nong Ham
              </v-card-title>

              <v-card-text class="pa-4">
                <v-alert
                  type="warning"
                  dense
                  outlined
                  class="mb-4"
                >
                  <small>Select a file from the dropdown to delete all related documents from PurchaseInvoices and PurchaseInvoiceFiles collections. Files are fetched from Company: {{ companyId }}</small>
                </v-alert>

                <v-progress-linear
                  v-if="isLoadingFiles && fileNames.length === 0"
                  indeterminate
                  color="primary"
                  class="mb-4"
                ></v-progress-linear>

                <v-form @submit.prevent="handleDelete">
                  <v-row>
                    <v-col cols="12" md="8">
                      <v-autocomplete
                        v-model="selectedFileNames"
                        :items="fileNames"
                        :loading="isLoadingFiles"
                        :search-input.sync="searchInput"
                        :label="`Select Files to Delete (${fileNames.length} files available)`"
                        placeholder="Type to search and select multiple files..."
                        outlined
                        dense
                        clearable
                        multiple
                        chips
                        small-chips
                        deletable-chips
                        :rules="[v => (v && v.length > 0) || 'At least one file must be selected']"
                        prepend-inner-icon="mdi-file-multiple"
                        no-data-text="No files found"
                        item-text="fileName"
                        item-value="fileName"
                      >
                        <template v-slot:selection="{ item, index }">
                          <v-chip
                            v-if="index < 3"
                            small
                            close
                            @click:close="removeFile(item)"
                            color="error"
                            text-color="white"
                          >
                            {{ item.fileName }}
                          </v-chip>
                          <span
                            v-if="index === 3"
                            class="grey--text caption"
                          >
                            (+{{ selectedFileNames.length - 3 }} others)
                          </span>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>{{ item.fileName }}</v-list-item-title>
                            <v-list-item-subtitle v-if="item.createdAt">
                              Created: {{ formatDate(item.createdAt) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-btn
                        color="error"
                        @click="showConfirmDialog = true"
                        :disabled="!selectedFileNames || selectedFileNames.length === 0 || isDeleting"
                        :loading="isDeleting"
                        depressed
                        block
                        class="mb-2"
                      >
                        <v-icon left small>mdi-delete</v-icon>
                        Delete {{ selectedFileNames.length }} File{{ selectedFileNames.length !== 1 ? 's' : '' }}
                      </v-btn>
                      <div>
                        <v-btn
                          @click="resetForm"
                          :disabled="isDeleting"
                          outlined
                          small
                          class="mr-2"
                        >
                          Clear
                        </v-btn>
                        <v-btn
                          @click="selectAll"
                          :disabled="isDeleting"
                          outlined
                          small
                          color="warning"
                          class="mr-2"
                        >
                          Select All
                        </v-btn>
                        <v-btn
                          @click="fetchFileNames"
                          :loading="isLoadingFiles"
                          outlined
                          small
                          color="primary"
                        >
                          <v-icon left small>mdi-refresh</v-icon>
                          Refresh
                        </v-btn>
                      </div>
                    </v-col>
                  </v-row>
                </v-form>

                <v-alert
                  v-if="result"
                  :type="result.success ? 'success' : 'warning'"
                  class="mt-4"
                  dense
                  dismissible
                  @input="result = null"
                >
                  <div v-if="result.success">
                    <strong>{{ result.message }}</strong>
                    <div v-if="result.details" class="mt-2">
                      <div class="text-caption">
                        Total deleted: {{ result.details.purchaseInvoices }} from PurchaseInvoices, {{ result.details.purchaseInvoiceFiles }} from PurchaseInvoiceFiles
                      </div>
                      <div v-if="result.details.fileResults && result.details.fileResults.length > 0" class="mt-1">
                        <v-simple-table dense v-if="result.filesProcessed > 1">
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th class="text-left">File Name</th>
                                <th class="text-center">Invoices</th>
                                <th class="text-center">Files</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(file, index) in result.details.fileResults" :key="index">
                                <td class="text-caption">{{ file.fileName }}</td>
                                <td class="text-center">{{ file.purchaseInvoices }}</td>
                                <td class="text-center">{{ file.purchaseInvoiceFiles }}</td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    {{ result.message }}
                  </div>
                </v-alert>

                <v-alert
                  v-if="error"
                  type="error"
                  class="mt-4"
                  dense
                  dismissible
                  @input="error = null"
                >
                  {{ error }}
                </v-alert>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-dialog v-model="showConfirmDialog" max-width="500">
          <v-card>
            <v-card-title class="error white--text pa-3">
              <v-icon dark small class="mr-2">mdi-alert</v-icon>
              Confirm Deletion of {{ selectedFileNames.length }} File{{ selectedFileNames.length !== 1 ? 's' : '' }}
            </v-card-title>
            <v-card-text class="pa-4">
              <div class="text-body-2 mb-3">
                <strong>Warning:</strong> This action cannot be undone!
              </div>
              <div class="text-body-2 mb-2">
                You are about to delete all documents for the following file{{ selectedFileNames.length !== 1 ? 's' : '' }}:
              </div>
              <v-card flat class="grey lighten-5 pa-2" style="max-height: 200px; overflow-y: auto;">
                <v-chip
                  v-for="(file, index) in selectedFileNames"
                  :key="index"
                  small
                  color="error"
                  outlined
                  class="ma-1"
                >
                  {{ file }}
                </v-chip>
              </v-card>
              <div class="text-caption grey--text mt-2">
                This will delete from: PurchaseInvoices & PurchaseInvoiceFiles collections
              </div>
            </v-card-text>
            <v-card-actions class="pa-3">
              <v-spacer></v-spacer>
              <v-btn
                text
                small
                @click="showConfirmDialog = false"
                :disabled="isDeleting"
              >
                Cancel
              </v-btn>
              <v-btn
                color="error"
                small
                depressed
                @click="performDelete"
                :loading="isDeleting"
              >
                <v-icon left small>mdi-delete</v-icon>
                Delete {{ selectedFileNames.length }} File{{ selectedFileNames.length !== 1 ? 's' : '' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
    </v-container>
    </v-main>
  </Admin>
</template>

<script>
import Admin from "@/components/admin/admin-main-template";
import firebase from "firebase/app";
import { mapState } from "vuex";
import moment from "moment";

export default {
  name: "documents.accountspayable.delete",
  components: {
    Admin,
  },
  data() {
    return {
      selectedFileNames: [],
      companyId: "",
      isDeleting: false,
      isLoadingFiles: false,
      showConfirmDialog: false,
      result: null,
      error: null,
      fileNames: [],
      searchInput: "",
    };
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      currentCompany: state => state.company.currentCompany,
    }),
  },
  mounted() {
    // Get companyId from route params or use default
    this.companyId = this.$route.params.companyId || "1VayvwUEotdYT4bjjTWc";
    this.fetchFileNames();
  },
  methods: {
    async fetchFileNames() {
      this.isLoadingFiles = true;
      this.error = null;

      try {
        const db = firebase.firestore();
        const snapshot = await db
          .collection('Companies')
          .doc(this.companyId)
          .collection('PurchaseInvoiceFiles')
          .get();

        const uniqueFileNames = new Map();

        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.fileName && !uniqueFileNames.has(data.fileName)) {
            uniqueFileNames.set(data.fileName, {
              fileName: data.fileName,
              createdAt: data.createdAt || null,
              id: doc.id
            });
          }
        });

        this.fileNames = Array.from(uniqueFileNames.values()).sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return b.createdAt.seconds - a.createdAt.seconds;
          }
          return a.fileName.localeCompare(b.fileName);
        });

        if (this.fileNames.length === 0) {
          this.error = "No files found in PurchaseInvoiceFiles collection";
        }
      } catch (err) {
        console.error("Error fetching file names:", err);
        this.error = "Failed to fetch file names: " + err.message;
      } finally {
        this.isLoadingFiles = false;
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return '';
      if (timestamp.toDate) {
        return moment(timestamp.toDate()).format('YYYY-MM-DD HH:mm');
      }
      return '';
    },
    removeFile(item) {
      const index = this.selectedFileNames.indexOf(item.fileName);
      if (index > -1) {
        this.selectedFileNames.splice(index, 1);
      }
    },
    selectAll() {
      this.selectedFileNames = this.fileNames.map(f => f.fileName);
    },
    showConfirmation() {
      if (!this.selectedFileNames || this.selectedFileNames.length === 0) {
        this.error = "Please select at least one file";
        return;
      }
      if (!this.companyId) {
        this.error = "Company ID not found. Please refresh the page.";
        return;
      }
      this.showConfirmDialog = true;
    },
    async performDelete() {
      this.showConfirmDialog = false;
      this.isDeleting = true;
      this.result = null;
      this.error = null;

      try {
        const deletePurchaseInvoice = firebase
          .functions()
          .httpsCallable("deletePurchaseInvoiceByFileName");

        const response = await deletePurchaseInvoice({
          fileNames: this.selectedFileNames,
          companyId: this.companyId,
        });

        this.result = response.data;

        if (response.data.success) {
          // Clear the selection and refresh the file list
          this.selectedFileNames = [];
          this.searchInput = "";

          // Refresh the file list to remove deleted files
          await this.fetchFileNames();
        }
      } catch (err) {
        console.error("Error deleting documents:", err);
        this.error = err.message || "Failed to delete documents";
      } finally {
        this.isDeleting = false;
      }
    },
    handleDelete() {
      if (this.selectedFileNames && this.selectedFileNames.length > 0 && this.companyId) {
        this.showConfirmation();
      }
    },
    resetForm() {
      this.selectedFileNames = [];
      this.searchInput = "";
      this.result = null;
      this.error = null;
    },
  },
};
</script>

<style scoped>
.v-alert {
  word-break: break-word;
}

@media (max-width: 960px) {
  .v-btn {
    margin-bottom: 8px;
  }
}
</style>